import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { CONTACT_PAGE_ANCHOR_JUMP_POINTS, DEVIATION_PATHS, PROJECTS, URLS, USER_PROFILE_SECTION_PATHS } from './constants';
import ddt from '@wix/da-ddt';
import trim from 'lodash/trim';
import omitBy from 'lodash/omitBy';
export const ALL_FOLDER_ID = -1;
export const SCRAPS_FOLDER_ID = -2;
export const ALL_FOLDER_NAME = 'All';
export const SCRAPS_FOLDER_NAME = 'Scraps';
function getPathParts(rawPathParts) {
    return rawPathParts.filter(Boolean).join('/');
}
function toQueryStringParams(obj) {
    const params = new URLSearchParams();
    for(const key in obj){
        if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
            params.set(key, obj[key]);
        }
    }
    return params.toString();
}
function getQueryString(obj) {
    const params = toQueryStringParams(obj);
    return params.length > 0 ? `?${params}` : '';
}
class Url {
    setEnv(isProduction, environmentFlags = []) {
        this.isProduction = isProduction;
        if (environmentFlags.length) {
            this.environmentFlags = environmentFlags;
        }
    }
    setBase(protocol, domain, mountPointPath, port) {
        this.protocol = protocol;
        this.domain = domain;
        this.mountPointPath = mountPointPath;
        this.port = port;
    }
    initServerSide(req) {
        this.initSSRDone = true;
        try {
            const expressProtocol = process.env.NODE_ENV === 'production' ? 'https' : req.protocol;
            const parsed = new URL(`${expressProtocol}://${req.get('host')}${req.originalUrl}`);
            const path = `${parsed.pathname}${parsed.search}`;
            this.setBase(parsed.protocol === 'http:' ? 'http' : 'https', parsed.hostname, path.replace(req.url, ''), Number(parsed.port));
        } catch (e) {}
    }
    initClientSide() {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }
        const config = window['__URL_CONFIG__'];
        if (!config) {
            ddt.error('URL', 'No clientside URL config found!');
            return;
        }
        this.setBase(config.protocol, config.domain, config.mountPointPath, config.port);
        this.environmentFlags = config.environmentFlags;
        this.isProduction = config.isProduction;
    }
    toConfig() {
        const { protocol, domain, mountPointPath, port, environmentFlags, isProduction } = this;
        return {
            protocol,
            domain,
            port,
            mountPointPath,
            environmentFlags,
            isProduction
        };
    }
    baseUrl(trailing = true) {
        const port = this.port ? `:${this.port}` : '';
        const url = `${this.protocol}://${this.domain}${port}${this.mountPointPath}`;
        if (trailing) {
            return `${url.replace(/\/$/, '')}/`;
        }
        return url.replace(/\/$/, '');
    }
    origin() {
        const port = this.port ? `:${this.port}` : '';
        return `${this.protocol}://${this.domain}${port}`;
    }
    /**
   * Builds a url that works on both prod and VM
   *
   * @param path path or full URL
   * @param baseUrl default is ROOT
   * @returns string
   */ buildFromPath(path = '', baseUrl = '') {
        let url = baseUrl ? baseUrl : URLS.ROOT;
        if (path && path.includes('://')) {
            // path is a full URL, use that instead
            url = path;
            path = '';
        }
        if (!this.isProduction) {
            url = url.replace('deviantart.com', 'deviantart.lan');
            url = url.replace('sta.sh', 'sta.nk');
        }
        if (path) {
            if (url.endsWith('/') || path.startsWith('/')) {
                url += path;
            } else {
                url += `/${path}`;
            }
        }
        // If we set the domain to .lan, replace all .coms
        if (this.domain === 'www.deviantart.lan') {
            url = url.replace('deviantart.com', 'deviantart.lan');
        }
        return url;
    }
    /**
   * Use this instead of URLS.N directly so VM links work nicely.
   *
   * Remember if your url takes options, create a helper instead.
   *
   * @param globalUrl
   */ to(globalUrl) {
        return this.buildFromPath(globalUrl);
    }
    /** Like buildFromPath but replaces origin even if given a full url with a protocol */ replaceWithCurrentOrigin(url) {
        // TODO: there's some duplication here with the above
        // maybe try to make the protocol check in buildFromPath optional?
        const parsed = new URL(url);
        return `${this.origin()}${parsed.pathname}${parsed.search}`;
    }
    userLink(username, path = '', anchor = false) {
        if (!username) {
            return '';
        }
        username = username.toLowerCase();
        if (path !== '') {
            path = `/${path.replace(/^\//, '')}`;
        }
        const fullUrl = this.toEnvironmentUrl(`/${username}${path}`, PROJECTS.PROFILE);
        return anchor ? `${fullUrl}#content` : fullUrl;
    }
    userGalleryLink(params) {
        return this.toEnvironmentUrl(this.userGalleryPath(params), PROJECTS.PROFILE);
    }
    userGalleryPath({ username, gallectionType, folderId, folderName, sortOrder, searchQuery, anchor, deviationIdSeed }) {
        const pathParts = [
            (username !== null && username !== void 0 ? username : '').toLowerCase(),
            {
                gallery: USER_PROFILE_SECTION_PATHS.GALLERY,
                collection: USER_PROFILE_SECTION_PATHS.FAVOURITES,
                private_collection: USER_PROFILE_SECTION_PATHS.PRIVATE_COLLECTIONS
            }[gallectionType]
        ];
        if (!searchQuery && folderId && folderName) {
            if (![
                ALL_FOLDER_ID,
                SCRAPS_FOLDER_ID
            ].includes(folderId)) {
                // ^ these folders are identified by name
                pathParts.push(`${folderId}`);
            }
            pathParts.push(folderName.toLowerCase().replace(/[^A-Za-z0-9]+/g, '-'));
        }
        const query = getQueryString({
            q: searchQuery ? searchQuery : undefined,
            sort: folderId === ALL_FOLDER_ID || searchQuery ? sortOrder : undefined,
            deviationid: deviationIdSeed
        });
        return `/${pathParts.join('/')}${query}${anchor ? '#content' : ''}`;
    }
    premiumGalleryPurchasePath(folderId) {
        return `/premium_folders/modal/purchase/${folderId}`;
    }
    contactLink() {
        return this.toEnvironmentUrl('/contact-us', PROJECTS.PONIES);
    }
    directedContactLink(menuId, anchorJumpId) {
        return this.toEnvironmentUrl(`/contact-us?menuId=${menuId}${anchorJumpId ? `#${anchorJumpId}` : ''}`, PROJECTS.PONIES);
    }
    contactDmcaHolderLink() {
        return this.directedContactLink('copyright_dmca_holder', CONTACT_PAGE_ANCHOR_JUMP_POINTS.ISSUE_RESPONSE);
    }
    contactDmcaCounterLink() {
        return this.directedContactLink('copyright_dmca_counter', CONTACT_PAGE_ANCHOR_JUMP_POINTS.ISSUE_RESPONSE);
    }
    dreamupLink(params = {}) {
        return this.toEnvironmentUrl(`/dreamup${Object.keys(params).length > 0 ? `?${Object.entries(params).map(([key, value])=>`${key}=${value}`).join('&')}` : ''}`, PROJECTS.STASH);
    }
    muroLink(params = {}) {
        return this.toEnvironmentUrl(`/muro/${Object.keys(params).length > 0 ? `?${Object.entries(params).map(([key, value])=>`${key}=${value}`).join('&')}` : ''}`);
    }
    userSubscriptionsLink(params) {
        return this.toEnvironmentUrl(this.userSubscriptionsPath(params), PROJECTS.PROFILE);
    }
    userSubscriptionsPath({ username, tierDeviationId, tierContentType, anchor, landedFromCheckout }) {
        const pathParts = [
            username.toLowerCase(),
            'subscriptions'
        ];
        if (tierDeviationId) {
            pathParts.push(`${tierDeviationId}`);
            if (tierContentType) {
                pathParts.push(tierContentType);
            }
        }
        let path = `/${pathParts.join('/')}`;
        if (landedFromCheckout) {
            path += '?checkout=1';
        }
        return `${path}${anchor ? '#content' : ''}`;
    }
    userSubscriptionsDeeplink({ username, tierEntityId, anchor = false }) {
        const sectionUrl = new URL(this.userLink(username, USER_PROFILE_SECTION_PATHS.SUBSCRIPTIONS, anchor));
        sectionUrl.searchParams.set('tier', tierEntityId);
        return sectionUrl.href;
    }
    userPostsLink(params) {
        return this.toEnvironmentUrl(this.userPostsPath(params), PROJECTS.PROFILE);
    }
    userPostsPath({ username, subPath, sortOrder, anchor }) {
        const pathParts = [
            username.toLowerCase(),
            USER_PROFILE_SECTION_PATHS.POSTS,
            subPath,
            sortOrder
        ].filter((value)=>value);
        return `/${pathParts.join('/')}${anchor ? '#content' : ''}`;
    }
    giveCoreMembership(username) {
        return this.toEnvironmentUrl(`${[
            URLS.CORE_MEMBERSHIP
        ]}/?mx=gift&subscribe=${username.toLowerCase()}`, PROJECTS.PONIES);
    }
    coreMembershipLink(referer, params) {
        let baseUrl = URLS.CORE_MEMBERSHIP;
        if (params === null || params === void 0 ? void 0 : params.forUserId) {
            baseUrl += '/gift';
        } else {
            baseUrl += '/buy';
        }
        const url = new URL(baseUrl);
        if (referer) {
            url.searchParams.set('referer', referer);
        }
        if ((params === null || params === void 0 ? void 0 : params.subproductId) !== undefined) {
            url.searchParams.set('id', params.subproductId.toString());
        }
        return this.toEnvironmentUrl(url.pathname + url.search, PROJECTS.PONIES);
    }
    userShopLink(username, subPath, query, anchor) {
        let fullUrl;
        if (Object.keys(query !== null && query !== void 0 ? query : {}).length > 0) {
            const q = new URLSearchParams(query);
            fullUrl = this.toEnvironmentUrl(`${this.userShopPath(username, subPath)}?${q.toString()}`, PROJECTS.PROFILE);
        } else {
            fullUrl = this.toEnvironmentUrl(`${this.userShopPath(username, subPath)}`, PROJECTS.PROFILE);
        }
        return anchor ? `${fullUrl}#content` : fullUrl;
    }
    userShopPath(username, subPath, anchor) {
        return `/${[
            username.toLowerCase(),
            USER_PROFILE_SECTION_PATHS.SHOP,
            subPath
        ].filter((value)=>value).join('/')}${anchor ? '#content' : ''}`;
    }
    // mirroring the php logic here
    filterDeviationTitle(title) {
        let filtered = title.replace(/[^A-Za-z0-9]+/g, '-');
        filtered = trim(filtered, '-');
        if (!filtered) {
            return '-';
        }
        return filtered;
    }
    deviationPath(deviationId, authorName = '', deviationTitle = '', base = DEVIATION_PATHS.DEVIATION, landingAction = undefined) {
        let queryString = '';
        if (landingAction) {
            queryString = `?action=${landingAction}`;
        }
        if (!authorName) {
            // We don't know the author, use the global redirector to find the correct url :)
            return `/deviation/${deviationId}${queryString}`;
        } else {
            authorName = authorName.toLowerCase();
        }
        if (deviationTitle) {
            deviationTitle = `${this.filterDeviationTitle(deviationTitle)}-`;
        }
        return `/${authorName}/${base}/${deviationTitle}${deviationId}${queryString}`;
    }
    deviationLink(deviationId, authorName = '', deviationTitle = '', base = DEVIATION_PATHS.DEVIATION, landingAction = undefined) {
        return this.toEnvironmentUrl(this.deviationPath(deviationId, authorName, deviationTitle, base, landingAction), PROJECTS.DEVIATION);
    }
    /**
   * Runs the PapiDeviation url field through our usual environment conversions and provides a landing action option like
   * the usual {@link deviationLink}.
   *
   * Useful in cases we need to link to either a published deviation or stash deviation and we do not have access to the
   * stashPrivateid field. Usual in user profiles or notifications.
   */ deviationLinkFromUrl(url, landingAction = undefined) {
        return this.toEnvironmentUrl(landingAction ? `${url}?action=${landingAction}` : url, PROJECTS.DEVIATION);
    }
    /**
   * Adoptable source files are hidden behind a redirect. This generates that redirect URL. Backend does some checks to validate access, then redirects the user.
   */ adoptableSourceFileLink(deviationId) {
        return this.buildFromPath(`/deviation/adoptable_src/${deviationId}`);
    }
    moreLikeThisLink(seedDeviationId) {
        return this.toEnvironmentUrl(`${URLS.MLT}/${seedDeviationId}`, PROJECTS.BROWSE);
    }
    moreLikeThisCollectionsLink(seedDeviationId) {
        return this.toEnvironmentUrl(`${URLS.MLT}/collections/${seedDeviationId}`, PROJECTS.BROWSE);
    }
    moreLikeThisArtistsLink(seedDeviationId) {
        return this.toEnvironmentUrl(`${URLS.MLT}/artists/${seedDeviationId}`, PROJECTS.BROWSE);
    }
    /** Links to the browse tag page when given a string tag */ tagsPageLink(tag, params = {}) {
        return this.toEnvironmentUrl(`${URLS.TAG}/${tag}${getQueryString(params)}`, PROJECTS.BROWSE);
    }
    /** Links to the daily challenges tag page for today */ dailyChallengesLink() {
        return this.toEnvironmentUrl('/dailychallenges', PROJECTS.BROWSE);
    }
    forumLink(forumPath, threadId) {
        const forumLink = `${URLS.FORUM}/${forumPath}/`;
        return threadId ? `${forumLink}${threadId}` : forumLink;
    }
    printLink(deviationId) {
        return `${URLS.ROOT}/print/view/${deviationId}`;
    }
    watchLink() {
        return this.toEnvironmentUrl('/notifications/watch', PROJECTS.MESSAGECENTRE);
    }
    feedbackLink() {
        return this.toEnvironmentUrl(`/notifications`, PROJECTS.MESSAGECENTRE);
    }
    groupNotificationsLink(groupId) {
        return this.toEnvironmentUrl(`/notifications?groupId=${groupId}`, PROJECTS.MESSAGECENTRE);
    }
    groupBackroomLink(username, section, subPath, queryParams) {
        return this.toEnvironmentUrl(this.groupBackroomPath(username, section, subPath, queryParams), PROJECTS.PROFILE);
    }
    groupBackroomPath(username, section, subPath, queryParams) {
        return `/${[
            username.toLowerCase(),
            USER_PROFILE_SECTION_PATHS.BACKROOM,
            section,
            subPath
        ].filter((value)=>value).join('/')}${getQueryString(queryParams || {})}`;
    }
    groupRosterLink(username) {
        return this.toEnvironmentUrl(`/${username}/admin/roster?standalone=1`);
    }
    protectLink(messageId) {
        return this.toEnvironmentUrl(`/notifications/message/nftprotect/${messageId}`, PROJECTS.FEEDBACK);
    }
    browseHomeLink(feedType) {
        return this.toEnvironmentUrl(`/${feedType !== null && feedType !== void 0 ? feedType : ''}`, PROJECTS.BROWSE);
    }
    browseDeviantsYouWatchLink(feedType = 'deviations', username) {
        return this.toEnvironmentUrl(`/watch${username ? `/${username.toLowerCase()}` : ''}/${feedType}`, PROJECTS.BROWSE);
    }
    browsePopularLink(feedType = 'deviations') {
        return this.toEnvironmentUrl(`/popular/${feedType}`, PROJECTS.BROWSE);
    }
    browseNewestLink(feedType = 'deviations') {
        return this.toEnvironmentUrl(`/new/${feedType}`, PROJECTS.BROWSE);
    }
    browseGroupFeedLink(groupName, feedType = 'deviations') {
        return this.toEnvironmentUrl(`/groups/${groupName}/${feedType}`, PROJECTS.BROWSE);
    }
    searchLink(q, basePath = '/search') {
        const path = `${basePath}${q ? getQueryString({
            q
        }) : ''}`;
        return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
    }
    browsePostsLink() {
        return this.toEnvironmentUrl('/posts', PROJECTS.BROWSE);
    }
    browseCommissionsLink() {
        return this.toEnvironmentUrl('/commissions', PROJECTS.BROWSE);
    }
    browseTopicsLink(topic) {
        const topicPath = topic ? `/${topic}` : '';
        return this.toEnvironmentUrl(`/topic${topicPath}`, PROJECTS.BROWSE);
    }
    browseDailyDeviationsLink(rawDate) {
        let date;
        if (rawDate) {
            const fullYear = rawDate.getFullYear();
            const month = rawDate.getMonth() + 1;
            const day = rawDate.getDate();
            const twoDigitMonth = month > 9 ? month : `0${month}`;
            const dateOfMonth = day > 9 ? day : `0${day}`;
            date = `${fullYear}-${twoDigitMonth}-${dateOfMonth}`;
        }
        const path = `/daily-deviations${getQueryString({
            date
        })}`;
        return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
    }
    browseJournalsLink({ order, faucet2, popularWindow } = {}) {
        const pathParts = getPathParts([
            'journals',
            faucet2
        ]);
        const path = `/${pathParts}${getQueryString({
            order,
            popularWindow
        })}`;
        return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
    }
    commentLink(commentableTypeId, commentableItemId, commentId, editId = undefined, embed = false, openReply = undefined) {
        const path = [
            commentableTypeId,
            commentableItemId,
            commentId,
            editId
        ].filter(Boolean);
        if (embed) {
            path.unshift('embed');
        }
        path.unshift('comments');
        return this.toEnvironmentUrl(`/${path.join('/')}${openReply ? `?reply_to=${commentId}` : ''}`, PROJECTS.COMMENTS);
    }
    loginLink() {
        return this.toEnvironmentUrl('/users/login', PROJECTS.SISU);
    }
    joinLink(joinpoint, returnTo, returnType, options) {
        // The 'joinpoint' query param here is used by BI to
        // track where the signup link came from.
        const query = getQueryString(_object_spread({
            joinpoint,
            referer: returnTo,
            refererType: returnType
        }, omitBy({
            da_dreamup: (options === null || options === void 0 ? void 0 : options.daDreamup) ? 1 : undefined
        }, (value)=>value === undefined)));
        return this.toEnvironmentUrl(// the slash at the end is required to avoid a double redirect by haproxy
        `/join/${query}`, PROJECTS.SISU);
    }
    notesHomeLink() {
        return this.toEnvironmentUrl(`/notifications/notes/`);
    }
    newNoteLink() {
        return `${this.notesHomeLink()}#to=`;
    }
    groupnoteLink(username, noteId) {
        return this.userLink(username, `notes/${noteId}`);
    }
    groupLogLink(username, messageId) {
        return this.userLink(username, `messages/${messageId}/`);
    }
    badgesLink(username, badgeId) {
        return this.userLink(username, 'badges') + (badgeId ? `/${badgeId}/` : '');
    }
    userNoteLink(username) {
        return this.toEnvironmentUrl(`${URLS.NOTES}/#to=${username.toLowerCase()}`);
    }
    chatroomInStandalone(chatroomId, tab) {
        const tabQuery = tab ? `&tab=${tab}` : '';
        return this.toEnvironmentUrl(`${URLS.CHAT_HOME}?room=${chatroomId}${tabQuery}`, PROJECTS.CHAT);
    }
    directMessageInStandalone(userUuid) {
        return this.toEnvironmentUrl(`${URLS.CHAT_HOME}?dm=${userUuid}`, PROJECTS.CHAT);
    }
    staticCDNLink(resource) {
        return `${URLS.CDN}/${resource}`;
    }
    checkoutLink(subProductId, foruserid) {
        return this.toEnvironmentUrl(`/purchase${getQueryString({
            id: subProductId,
            foruserid
        })}`, PROJECTS.PONIES);
    }
    checkoutCompleteLink(orderGroupId, referer) {
        return this.toEnvironmentUrl(`/purchase/complete${getQueryString({
            id: orderGroupId,
            referer
        })}`, PROJECTS.PONIES);
    }
    earningsDownloadLink(month, year) {
        return this.toEnvironmentUrl(URLS.EARNINGS_DOWNLOAD, PROJECTS.PONIES) + (month && year ? getQueryString({
            month,
            year
        }) : '');
    }
    account(tierId) {
        return this.toEnvironmentUrl(`/account${tierId ? `?tier=${tierId}` : ''}`, PROJECTS.PONIES);
    }
    customerDashboard(subproductId) {
        return this.toEnvironmentUrl(`/account/customers${subproductId ? `?subproductid=${subproductId}` : ''}`, PROJECTS.PONIES);
    }
    /**
   * Provided a deviation's id, this will return a link to our submit page with the submission prepoulated with said deviation.
   *
   * This is useful for products built on top of stash.
   */ submissionLinkWithId(deviationId) {
        return this.toEnvironmentUrl(`/_deviation_submit?deviationid=${deviationId}`);
    }
    submissionLinkWithGroupId(groupId) {
        return this.studio({
            openSubmitForm: true
        });
    }
    studio(options = {}) {
        const params = new URLSearchParams();
        const { section = 'drafts', playground, discounts, published, products, searchQuery, openSubmitForm, tierId, contentType } = options;
        let path;
        if ([
            'root',
            'dreamup',
            'drawings',
            'inspiration'
        ].includes(playground !== null && playground !== void 0 ? playground : '')) {
            switch(playground){
                case 'dreamup':
                    path = '/studio/dreamup';
                    break;
                case 'drawings':
                    path = '/studio/drawings';
                    break;
                case 'inspiration':
                    path = '/studio/inspiration';
                    break;
                case 'root':
                default:
                    path = '/studio/playground';
                    break;
            }
        } else if (section === 'published' || [
            'root',
            'galleries',
            'tiers'
        ].includes(published !== null && published !== void 0 ? published : '')) {
            switch(published){
                case 'galleries':
                    path = '/studio/published/galleries';
                    break;
                case 'tiers':
                    path = '/studio/published/tiers';
                    break;
                case 'root':
                default:
                    path = '/studio/published';
                    if (options.folderId) {
                        path = `${path}/${options.folderId}`;
                    }
                    if (options.contentType) {
                        path = `${path}/${contentType}`;
                    }
            }
        } else if (products) {
            switch(products){
                case 'tier':
                    path = '/studio/subscriptions';
                    break;
                case 'tier-unlisted':
                    path = '/studio/subscriptions-drafts';
                    break;
                case 'commission':
                    path = '/studio/commissions';
                    break;
                case 'exclusive':
                    path = '/studio/exclusives';
                    break;
                case 'exclusive-unlisted':
                    path = '/studio/unlisted-exclusives';
                    break;
                case 'premium_gallery':
                    path = '/studio/premium-galleries';
                    break;
                case 'pcp':
                    path = '/studio/pcp';
                    break;
                case 'pcp-unlisted':
                    path = '/studio/unlisted-pcp';
                    break;
                case 'root':
                default:
                    path = '/studio/shop-products';
                    break;
            }
        } else if (section === 'drafts') {
            path = '/studio';
        } else if (section === 'literature') {
            path = '/studio/literature';
        } else if (section === 'journals') {
            path = '/studio/journals';
        } else if (section === 'scheduled') {
            path = '/studio/scheduled';
        } else if (section === 'shop-reports') {
            path = '/studio/shop-reports';
        } else if (section === 'stash') {
            path = '/stash';
            if (options.folderId) {
                path = `/stash/${options.folderId}`;
            }
        } else if ([
            'active',
            'scheduled',
            'ended',
            'active_and_scheduled'
        ].includes(discounts !== null && discounts !== void 0 ? discounts : '')) {
            switch(discounts){
                case 'active':
                    path = '/studio/discounts/active';
                    break;
                case 'scheduled':
                    path = '/studio/discounts/scheduled';
                    break;
                case 'ended':
                    path = '/studio/discounts/ended';
                    break;
                case 'active_and_scheduled':
                    path = '/studio/discounts/active_and_scheduled';
                    break;
                default:
                    break;
            }
        }
        // all stash sections support search
        if (searchQuery) {
            params.append('q', searchQuery);
        }
        if (openSubmitForm) {
            params.append('new', '1');
            if (tierId) {
                params.append('fortier', `${tierId}`);
            }
        }
        const querystring = params.size > 0 ? `?${params}` : '';
        return this.toEnvironmentUrl(`${path}${querystring}`, PROJECTS.EDITOR);
    }
    studioStash(folderId) {
        const encodedFolderId = folderId && `2${folderId.toString(36)}`;
        return this.toEnvironmentUrl(folderId ? `/stash/${encodedFolderId}` : '/stash', PROJECTS.EDITOR);
    }
    studioGallery(folderId) {
        return this.toEnvironmentUrl(folderId ? `/studio/published/${folderId}` : '/studio/published/galleries', PROJECTS.EDITOR);
    }
    studioTier(tierId, contentType) {
        return this.toEnvironmentUrl(`/studio/tier/${tierId}/${contentType}`, PROJECTS.EDITOR);
    }
    studioFolder(folderId, folderType) {
        switch(folderType){
            case 'stash':
                return this.studioStash(folderId);
            case 'gallery':
                return this.studioGallery(folderId);
        }
    }
    stashFolderZipDownload(folderId) {
        const encodedFolderId = `2${folderId.toString(36)}`;
        return `${URLS.STASH}zip/${encodedFolderId}`;
    }
    shopLink(path) {
        if (!path) {
            return URLS.SHOP;
        }
        return `${URLS.SHOP}/${path}`;
    }
    /**
   * Link to a stash item
   */ stashItemLink(base36Id, landingAction = undefined) {
        const id = base36Id.startsWith('0') ? base36Id : `0${base36Id}`;
        const stashUrl = this.toEnvironmentUrl(`/stash/${id}`, PROJECTS.DEVIATION);
        return landingAction ? `${stashUrl}?action=${landingAction}` : stashUrl;
    }
    /**
   * Link to stash folder
   */ stashFolderLink(folderId) {
        return this.to(`${URLS.STASH}2${folderId.toString(36)}`);
    }
    /**
   * Parse URL and return components:
   * - `path` - everything up to '?' symbol
   * - `query` - query part after the '?' symbol, excluding fragment if any
   * - `fragment` - fragment part after the '#' symbol
   *
   * @param url Absolute or relative URL
   * @returns object
   */ parseUrl(url) {
        const [pathWithQuery, fragment] = this._split(url, '#');
        const [path, query] = this._split(pathWithQuery, '?');
        return {
            path,
            fragment,
            query
        };
    }
    toEnvironmentUrl(path, projectFlag) {
        if (path.startsWith(URLS.ROOT)) {
            path = path.replace(/^https:\/\/www.deviantart.com/, '');
        }
        this.checkServerInit();
        if (typeof window !== 'undefined' && window['__XHR_LOCAL__'] || typeof process !== 'undefined' && process.env['XHR_ENV'] === 'vm') {
            // Client-side URLs to any project on the VM
            return `${this.origin()}${path}`;
        }
        if (projectFlag && this.hasEnvironmentFlag(projectFlag)) {
            // If the url belongs to the app we're running, point to it
            return `${this.baseUrl(false)}${path}`;
        } else if (!this.isProduction) {
            // Otherwise, if we're on vm, point to deviantart.lan domain
            return this.buildFromPath(path);
        }
        // Else, build path using GA as base path
        return `${URLS.ROOT}${path}`;
    }
    hasEnvironmentFlag(flag) {
        if (this.isProduction) {
            return false;
        }
        return Boolean((this.environmentFlags || []).find((v)=>v === flag));
    }
    checkServerInit() {
        if ((typeof document === 'undefined' || typeof window === 'undefined') && !this.initSSRDone) {
            ddt.log('URL', 'SSR url was not initialised before use, you probably have two instances');
        }
    }
    _split(s, separator) {
        const p = s.indexOf(separator);
        if (p >= 0) {
            return [
                s.substring(0, p),
                s.substring(p + separator.length)
            ];
        }
        return [
            s,
            ''
        ];
    }
    constructor(){
        _define_property(this, "isProduction", true);
        _define_property(this, "environmentFlags", []);
        _define_property(this, "protocol", void 0);
        _define_property(this, "domain", void 0);
        _define_property(this, "port", void 0);
        _define_property(this, "mountPointPath", void 0);
        _define_property(this, "initSSRDone", false);
        this.initClientSide();
    }
}
/**
 * This lib is very simple, its basically an ENUM of known public
 * canonical URLs. With some helpers to generate paths for those
 * known URLs.
 *
 * Additonally it provides a way to override local development env
 * with local urls, so that when developing locally you can link to
 * your localhost rather than production base url (www.da.com)
 *
 * If you are writing a helper method remember to use toEnvironment()
 * this will handle to .com/.lan rewriting but also handle local
 * dev where you are on node.deviantart.lan:3000 here the project
 * param allows us to detect you are running the project locally
 * and redirect link to node. instead of .lan
 *
 * buildFromPath can be used for adhoc links and only considers
 * .com vs .lan so no local dev handling.
 *
 * For global urls URLS.N use the to() helper to make them dev friendly.
 *
 * But remember that if your url takes common params, make a helper
 * that uses toEnvironment as its the most dev friendly way.
 */ export { Url as default };

//# sourceMappingURL=url.js.map